import { useFieldArray, useFormContext } from 'react-hook-form';
import { WithScope } from '@mews-commander/types';
import { useCommanderSelector } from '@mews-commander/utils';
import { getServicePerTimeUnitLocalization } from '@mews-commander/legacy-entities/src/utils/serviceTimeUnitLocalization';
import { DropdownItem } from '@optimus-web/core';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import {
    createZodFormResolver,
    FormInputField,
    FormSelectField,
    lt,
    SubmitHandler,
    useAppClockService,
    useFormControls,
} from '@mews/framework';
import { getTimeUnitByServiceId } from '@mews-commander/legacy-entities/src/ducks/services';
import { LocalizedUtcTimeUnit } from '@mews-commander/ui';
import { TimeUnit } from '@mews/enums';
import { TimeUnitPeriod } from '@mews-commander/data-access';
import { getCurrentCurrencyCultureInfo } from '@mews-commander/core/legacy-globalization';
import { FormLayout } from '../../FormLayout';
import { getRateFormServiceId } from '../selectors';
import { getPerTimeUnitRateInitialValues } from '../../../selectors/pricing';
import { getCurrentEnterpriseMinimalOperationEditableUtc } from '../../../../availabilityBlock/selectors';
import { PartialMonthDayRange } from '../../ItemsTab/PartialMonthDayRange';
import { getMinEditableUtc } from '../../../selectors';
import {
    RateExcludingProductsFormKeys,
    rateExcludingProductsFormSchema,
    RateExcludingProductsFormValues,
} from './types';
import { useRateExcludingProductsMutation } from './useRateExcludingProductsMutation';
import { getFilteredPerNight, mapPerNight } from './util';

interface RateExcludingProductsContainerProps extends WithScope {
    reservationIds: string[];
    onSuccess?: () => void;
}

const FORM_PREFIX = 'per-night-rate-form-';

export const RateExcludingProductsContainer = ({
    scope,
    reservationIds,
    onSuccess,
}: RateExcludingProductsContainerProps) => {
    const { acceptedCurrencyCodes, defaultCurrencyCode } = useCurrentEnterprise();
    const serviceId = useCommanderSelector((state) => getRateFormServiceId(state, { scope })) ?? '';
    const localizationInfo = useCommanderSelector(getCurrentCurrencyCultureInfo);

    const { getAppNowUtc } = useAppClockService();
    const minEditableUtcSubmit = useCommanderSelector((state) =>
        getMinEditableUtc(state, { appNowUtc: getAppNowUtc() })
    );
    const minEditableUtc = useCommanderSelector(getCurrentEnterpriseMinimalOperationEditableUtc);

    const timeUnit = useCommanderSelector((state) => getTimeUnitByServiceId(state, {}));
    const timeUnitText = useCommanderSelector((state) => getServicePerTimeUnitLocalization(state, { id: serviceId }));

    const defaultValues = useCommanderSelector((state) =>
        getPerTimeUnitRateInitialValues(state, { serviceId, serviceOrderIds: reservationIds })
    );

    const { mutate, isPending } = useRateExcludingProductsMutation();
    const onSubmit: SubmitHandler<RateExcludingProductsFormValues> = (v) => {
        const { perNight: editedPerNight, currency: editedCurrency, reason } = v;
        const { perNight: initialPerNight, currency: initialCurrency } = defaultValues;

        const perNight = getFilteredPerNight(
            editedPerNight,
            initialPerNight,
            editedCurrency,
            initialCurrency ?? defaultCurrencyCode
        );
        const productOrderAmountUpdates = mapPerNight(perNight, minEditableUtcSubmit, editedCurrency, localizationInfo);

        mutate(
            {
                productOrderAmountUpdates,
                reason,
            },
            {
                onSuccess,
            }
        );
    };

    // Creating the form control
    const { renderFormContent } = useFormControls<RateExcludingProductsFormValues>({
        onSubmit,
        resolver: createZodFormResolver(rateExcludingProductsFormSchema(localizationInfo)),
        values: {
            [RateExcludingProductsFormKeys.perNight]: defaultValues.perNight,
            [RateExcludingProductsFormKeys.currency]: defaultValues.currency ?? defaultCurrencyCode,
            [RateExcludingProductsFormKeys.reason]: '',
        },
        dataTestForm: `${FORM_PREFIX}${scope}`,
    });

    return renderFormContent(
        <RateExcludingProducts
            isLoading={isPending}
            timeUnit={timeUnit}
            timeUnitText={timeUnitText}
            acceptedCurrencyCodes={acceptedCurrencyCodes || []}
            minEditableUtc={minEditableUtc}
        />
    );
};

interface RateExcludingProductsProps {
    isLoading: boolean;
    timeUnitText: string;
    acceptedCurrencyCodes: string[];
    minEditableUtc: string;
    timeUnit: TimeUnit | TimeUnitPeriod;
}

const RateExcludingProducts = ({
    isLoading,
    timeUnitText,
    acceptedCurrencyCodes,
    minEditableUtc,
    timeUnit,
}: RateExcludingProductsProps) => {
    const { control } = useFormContext<RateExcludingProductsFormValues>();
    const { fields } = useFieldArray({
        control,
        name: RateExcludingProductsFormKeys.perNight,
    });

    return (
        <>
            <FormLayout
                title={lt('SetAverageTimeUnitRateExcludingProducts', {
                    timeUnit: timeUnitText,
                })}
                loading={isLoading}
                submitButtonLoading={isLoading}
            >
                <FormSelectField name={RateExcludingProductsFormKeys.currency} label={lt('Currency')} required>
                    {(acceptedCurrencyCodes || []).map((item) => (
                        <DropdownItem key={item} value={item} label={item} />
                    ))}
                </FormSelectField>
                {/* Start field array */}
                {fields.map(({ startUtc, consumedUtc, enabled, proRataStartUtc, proRataEndUtc, id }, index) => {
                    const isConsumed = consumedUtc && consumedUtc < minEditableUtc;
                    const isEditable = enabled && !isConsumed;

                    const fieldLabel =
                        proRataStartUtc && proRataEndUtc ? (
                            <PartialMonthDayRange proRataStartUtc={proRataStartUtc} proRataEndUtc={proRataEndUtc} />
                        ) : (
                            <LocalizedUtcTimeUnit timeUnit={timeUnit} utcString={startUtc ?? ''} />
                        );

                    return (
                        <FormInputField
                            key={id}
                            name={`${RateExcludingProductsFormKeys.perNight}.${index}${RateExcludingProductsFormKeys.perNightValue}`}
                            disabled={!isEditable}
                            required
                            label={fieldLabel}
                        />
                    );
                })}
                {/* End field array */}
                <FormInputField
                    name={RateExcludingProductsFormKeys.reason}
                    label={lt('ReservationDetailReasonInputLabel')}
                    required
                />
            </FormLayout>
        </>
    );
};
